import { useEffect, useState } from "react";
import api from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import { useAuth } from "@/lib/AuthContext";
import { PageHeader, Card, Btn, Pill } from "@/dashboard/ui";
import { Printer, Download } from "lucide-react";

export default function Reports() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [leads, setLeads] = useState(null);
  const [campaigns, setCampaigns] = useState(null);

  useEffect(() => {
    api.get("/dashboard/metrics").then((r) => setMetrics(r.data)).catch(() => setMetrics({}));
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
    api.get("/marketing/campaigns").then((r) => setCampaigns(r.data)).catch(() => setCampaigns([]));
  }, []);

  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const loading = metrics === null || leads === null || campaigns === null;

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" sub="A printable summary of your real CRM data, branded with your logo.">
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/summary.pdf`}
          data-testid="download-pdf-button"
          className="print:hidden inline-flex items-center justify-center gap-2 rounded-full border border-neutral-300 bg-white px-5 py-2.5 text-sm font-semibold text-[#111111] transition-colors hover:bg-neutral-50"
          onClick={async (e) => {
            e.preventDefault();
            try {
              const r = await api.get("/reports/summary.pdf", { responseType: "blob" });
              const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = "savy-kerno-summary.pdf";
              a.click();
              URL.revokeObjectURL(url);
            } catch { /* toast-free: button remains available */ }
          }}
        >
          <Download size={15} /> Download PDF
        </a>
        <Btn data-testid="print-report-button" onClick={() => window.print()} className="print:hidden">
          <Printer size={15} /> Print / Save PDF
        </Btn>
      </PageHeader>

      {loading ? (
        <p className="py-10 text-center text-sm text-neutral-400">Loading…</p>
      ) : (
        <Card className="p-8 sm:p-10" data-testid="report-document">
          <div className="flex items-center justify-between border-b border-neutral-200 pb-6">
            <div className="flex items-center gap-3">
              <BrandLogo className="h-10 w-10" />
              <div>
                <p className="text-lg font-extrabold tracking-tight text-[#111111]">Savy Kerno</p>
                <p className="text-xs text-neutral-500">Lead & Pipeline Report</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold text-neutral-600">{today}</p>
              <p className="text-xs text-neutral-400">{user?.email}</p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="report-metrics">
            {[
              { label: "Total Leads", value: metrics.total_leads },
              { label: "New", value: metrics.new_leads },
              { label: "Follow-ups Due", value: metrics.followups_due },
              { label: "Converted", value: metrics.converted_leads },
            ].map((m) => (
              <div key={m.label} className="rounded-xl border border-neutral-200 p-4 text-center">
                <p className="text-2xl font-extrabold text-[#111111]">{m.value ?? 0}</p>
                <p className="mt-0.5 text-[11px] font-medium text-neutral-500">{m.label}</p>
              </div>
            ))}
          </div>

          <p className="mb-3 mt-8 text-xs font-bold uppercase tracking-wider text-neutral-500">
            Leads ({leads.length})
          </p>
          {leads.length === 0 ? (
            <p className="rounded-xl border border-dashed border-neutral-300 px-4 py-8 text-center text-xs text-neutral-400">
              No leads yet — add some in CRM Leads and they'll appear on this report.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-left text-sm" data-testid="report-leads-table">
                <thead>
                  <tr className="border-b border-neutral-200 text-xs uppercase tracking-wider text-neutral-500">
                    <th className="py-2.5 pr-4 font-semibold">Name</th>
                    <th className="py-2.5 pr-4 font-semibold">Phone</th>
                    <th className="py-2.5 pr-4 font-semibold">Status</th>
                    <th className="py-2.5 pr-4 font-semibold">Follow-up</th>
                    <th className="py-2.5 font-semibold">Consent</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-neutral-100 last:border-0">
                      <td className="py-2.5 pr-4 font-medium text-[#111111]">{l.full_name}</td>
                      <td className="py-2.5 pr-4 text-neutral-600">{l.phone}</td>
                      <td className="py-2.5 pr-4"><Pill tone={l.status === "Lost" ? "red" : l.status === "New" ? "outline" : "grey"}>{l.status}</Pill></td>
                      <td className="py-2.5 pr-4 text-neutral-600">{l.follow_up_date || "—"}</td>
                      <td className="py-2.5 text-xs text-neutral-500">
                        {l.do_not_call ? "Do not call" : l.call_consent ? "Call consented" : "Not recorded"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="mb-3 mt-8 text-xs font-bold uppercase tracking-wider text-neutral-500">
            Marketing Campaigns ({campaigns.length})
          </p>
          {campaigns.length === 0 ? (
            <p className="rounded-xl border border-dashed border-neutral-300 px-4 py-8 text-center text-xs text-neutral-400">
              No marketing campaigns yet — create them in AI Marketing Studio. Nothing is sent or published until integrations connect.
            </p>
          ) : (
            <div className="space-y-2.5" data-testid="report-campaigns-list">
              {campaigns.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-neutral-200 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-[#111111]">{c.name}</p>
                    <p className="text-xs text-neutral-500">
                      {c.channel}{c.objective ? ` · ${c.objective}` : ""}{c.scheduled_date ? ` · Scheduled ${c.scheduled_date}` : ""}
                    </p>
                  </div>
                  <Pill tone={c.status === "Scheduled" ? "black" : "outline"}>{c.status}</Pill>
                </div>
              ))}
              <p className="pt-1 text-[11px] text-neutral-400">
                Campaigns are drafts or scheduled only — no send, publish or performance data exists until integrations connect.
              </p>
            </div>
          )}

          <p className="mt-8 border-t border-neutral-100 pt-4 text-center text-[11px] text-neutral-400">
            Generated by Savy Kerno · Figures are live counts from your CRM, not estimates.
          </p>
        </Card>
      )}
    </div>
  );
}
